/**
 * player-suggestions.ts - Player Suggestion Display and Selection
 *
 * Handles rendering search results and populating the form
 * when a player is selected from suggestions.
 */

import { escapeHtml } from '../../utils/security';
import type { PlayerSearchResult } from './player-search-parsers';

/**
 * Get the tournament's time control setting
 */
function getTournamentTimeControl(tournamentId: string): string {
  const activeButton = document.querySelector(`#time-control-${tournamentId} .segment-button.active`) as HTMLElement | null;
  return activeButton?.dataset['value'] || 'classical';
}

/**
 * Get the appropriate rating based on tournament time control
 */
function getRatingForTimeControl(player: PlayerSearchResult, timeControl: string): number {
  switch (timeControl) {
    case 'blitz':
      return player.ratingBlitz || player.ratingRapid || player.ratingStd || player.rating || 0;
    case 'rapid':
      return player.ratingRapid || player.ratingStd || player.ratingBlitz || player.rating || 0;
    case 'classical':
    default:
      return player.ratingStd || player.ratingRapid || player.ratingBlitz || player.rating || 0;
  }
}

/**
 * Get display rating based on tournament time control
 */
function getDisplayRating(player: PlayerSearchResult, tournamentId: string): number {
  const timeControl = getTournamentTimeControl(tournamentId);
  return getRatingForTimeControl(player, timeControl);
}

/**
 * Render a single player item HTML
 * @param player - The player to render
 * @param tournamentId - The tournament ID
 * @param showFideId - Whether to show FIDE ID or federation code
 * @returns HTML string for the player item
 */
function renderPlayerItem(player: PlayerSearchResult, tournamentId: string, showFideId: boolean): string {
  const displayName = `${escapeHtml(player.firstName)} ${escapeHtml(player.lastName)}`.trim();
  const displayRating = getDisplayRating(player, tournamentId);

  return `
    <div class="player-suggestion-item" data-player='${escapeHtml(JSON.stringify(player))}' data-tournament="${tournamentId}">
      <div class="player-suggestion-name">${player.title ? escapeHtml(player.title) + ' ' : ''}${displayName}</div>
      <div class="player-suggestion-details">
        <span class="player-rating">${displayRating}</span>
        <span class="player-federation">${player.federation ? escapeHtml(player.federation) : ''}</span>
        ${player.club ? `<span class="player-club">${escapeHtml(player.club)}</span>` : ''}
        ${showFideId
          ? (player.fideId ? `<span class="player-id">FIDE: ${player.fideId}</span>` : '')
          : (player.federationCode ? `<span class="player-id">Code: ${player.federationCode}</span>` : '')
        }
      </div>
    </div>
  `;
}

/**
 * Display player suggestions in the UI
 * @param fidePlayers - FIDE player results
 * @param swissChessPlayers - Swiss Chess player results
 * @param tournamentId - The tournament ID
 */
export function displayPlayerSuggestions(
  fidePlayers: PlayerSearchResult[],
  swissChessPlayers: PlayerSearchResult[],
  tournamentId: string
): void {
  const suggestionsDiv = document.getElementById(`player-suggestions-${tournamentId}`);
  const fideList = document.getElementById(`suggestions-fide-${tournamentId}`);
  const swissChessList = document.getElementById(`suggestions-swisschess-${tournamentId}`);

  if (!suggestionsDiv || !fideList || !swissChessList) return;

  // Display FIDE results
  if (fidePlayers.length === 0) {
    fideList.innerHTML = '<div class="player-no-results">No players found</div>';
  } else {
    fideList.innerHTML = fidePlayers.map(p => renderPlayerItem(p, tournamentId, true)).join('');
  }

  // Display Swiss Chess results
  if (swissChessPlayers.length === 0) {
    swissChessList.innerHTML = '<div class="player-no-results">No players found</div>';
  } else {
    swissChessList.innerHTML = swissChessPlayers.map(p => renderPlayerItem(p, tournamentId, false)).join('');
  }

  suggestionsDiv.style.display = 'block';
}

/**
 * Select a player from suggestions and populate the form
 * @param tournamentId - The tournament ID
 * @param firstName - Player's first name
 * @param lastName - Player's last name
 * @param rating - Player's rating
 * @param title - Player's title
 * @param club - Player's club
 * @param federation - Player's federation
 * @param fideId - Player's FIDE ID
 * @param federationCode - Player's federation code
 */
export function selectPlayer(
  tournamentId: string,
  firstName: string,
  lastName: string,
  rating: number,
  title: string | null,
  club: string | null,
  federation: string | null,
  fideId: string | null,
  federationCode: string | null
): void {
  const firstNameInput = document.getElementById(`player-first-name-${tournamentId}`) as HTMLInputElement | null;
  const lastNameInput = document.getElementById(`player-last-name-${tournamentId}`) as HTMLInputElement | null;
  const ratingInput = document.getElementById(`player-rating-${tournamentId}`) as HTMLInputElement | null;
  const titleInput = document.getElementById(`player-title-${tournamentId}`) as HTMLInputElement | null;
  const federationInput = document.getElementById(`player-federation-${tournamentId}`) as HTMLInputElement | null;
  const clubInput = document.getElementById(`player-club-${tournamentId}`) as HTMLInputElement | null;
  const fideIdInput = document.getElementById(`player-fide-id-${tournamentId}`) as HTMLInputElement | null;
  const federationCodeInput = document.getElementById(`player-federation-code-${tournamentId}`) as HTMLInputElement | null;
  const suggestionsDiv = document.getElementById(`player-suggestions-${tournamentId}`);

  if (!firstNameInput || !lastNameInput || !ratingInput || !suggestionsDiv) return;

  // Fill in the form
  firstNameInput.value = firstName || '';
  lastNameInput.value = lastName || '';
  ratingInput.value = rating ? String(rating) : '';
  if (titleInput) titleInput.value = title || '';
  if (federationInput) federationInput.value = federation || '';
  if (clubInput) clubInput.value = club || '';
  if (fideIdInput) fideIdInput.value = fideId || '';
  if (federationCodeInput) federationCodeInput.value = federationCode || '';

  // Store federation, fideId, and federationCode in data attributes on the add button for later use (as backup)
  const addButton = document.getElementById(`add-player-${tournamentId}`) as HTMLButtonElement | null;
  if (addButton) {
    if (federation) addButton.dataset['federation'] = federation;
    if (fideId) addButton.dataset['fideId'] = fideId;
    if (federationCode) addButton.dataset['federationCode'] = federationCode;
  }

  // Hide suggestions and reset scroll flag for next search
  suggestionsDiv.style.display = 'none';
  if (typeof window.resetSuggestionsScroll === 'function') {
    window.resetSuggestionsScroll(tournamentId);
  }

  // Focus on the add button
  if (addButton) {
    addButton.focus();
  }
}

// Export to window for onclick handlers

window.displayPlayerSuggestions = displayPlayerSuggestions;
window.selectPlayer = selectPlayer;
